import { Injectable, inject } from '@angular/core';
import { LogService } from '@viag/ngx-logger';

import { Guid, TimeUtil } from '@shared/utils';
import { TaskType } from '../types/task-type';
import { ClockService } from './clock.service';
import { ProjectService } from './project.service';
import { HourService } from './hour.service';
import { TimesheetService } from '../hours/timesheet.service';
import { filter, map, switchMap, withLatestFrom } from 'rxjs';
import { Hour } from '../types/hour';
import { Timesheet } from '../types/timesheet';

@Injectable({
  providedIn: 'root'
})
export class HourJourneyService {
  private log = inject(LogService);
  private clockService = inject(ClockService);
  private projectService = inject(ProjectService);
  private hourService = inject(HourService);
  private timesheetService = inject(TimesheetService);

  handleDeductions() {

    // GAV-Abzug (30min) bei erster Fahrt erstellen (wenn Projekt-Fahrzeit > 30min [Hin- und Rückfahrt])
    this.clockService.started$
      .pipe(
        filter(s => s.taskTypeCode === TaskType.Journey),
        withLatestFrom(this.projectService.projects$) // withLatestFrom holt beim Event von stoped$ den aktuellsten Wert aus projects$, triggert aber nicht wenn sich projects$ ändert
      )
      .subscribe(([started, projects]) => {
        const project = projects.find(p => p.projectNumber === started.projectNumber);
        const hasJourneyDeduction = project.journeyDeduction > 0;
        this.log.debug(`Journey started, HasJourneyDeduction ${hasJourneyDeduction}, JourneyDeduction: ${project.journeyDeduction}`, started);
        if (!hasJourneyDeduction) {
          return;
        }

        this.isFirstJourney(started.projectTaskHourId, started.startTime).subscribe(isFirstHour => {

          const journeyTime = (project.journeyTime || 0) * 2; // Hin- und Rückfahrt
          const journeyDeduction = project.journeyDeduction;
          this.log.info(`Started: isFirstHour ${isFirstHour}, journeyTime: ${journeyTime}`);

          if (isFirstHour && journeyTime > journeyDeduction) { // nur abziehen, wenn Reisezeit grösser als Abzug (30min)
            const journeyHour = this.createJourneyDeduction(started.projectTaskId, started.projectNumber, started.startTime, started.gpsData, journeyDeduction);
            this.saveHour(journeyHour);
          }
        });
      });

    // TOOD: 30min effektiv abziehen und nicht pauschal:
    // Siehe flow-chart
    // 1. clockService.started$ oben entfernen
    // 2. clockService.stoped$ refacoteren:
    // wir brauchen die Fahrtzeit ohne Mitarbeiter-Fahrtzeit:
    // filter home entfernen
    // durationInMinutes zurückgeben
    // createJourneyDeduciton für mitarbeteri nur wenn type home
    // aber zusätztlich journeyDeductionService prüfen ob Balance vorhanden ist und abzug imit durationInMinutes -> balance neu berechnen

    // Mitarbeiter Fahrtzeit-Abzüge wenn vom/zum Wohnort (journeyType = 'home')
    this.clockService.stoped$
      .pipe(
        filter(s => s.taskTypeCode === TaskType.Journey && s.journeyType === 'home'),
        withLatestFrom(this.projectService.projects$) // withLatestFrom holt beim Event von stoped$ den aktuellsten Wert aus projects$, triggert aber nicht wenn sich projects$ ändert
      )
      .subscribe(([stoped, projects]) => {
        const project = projects.find(p => p.projectNumber === stoped.projectNumber);
        this.log.debug('Home Journey stoped', stoped);

        const canDeduct = this.canJourneyDeduction(project.employeeJourneyTime, project.journeyTime, stoped.duration);
        if (!canDeduct) {
          return;
        }
        const journeyHour = this.createJourneyDeduction(stoped.projectTaskId, project.projectNumber, stoped.endTime, stoped.gpsData, project.employeeJourneyTime);
        this.saveHour(journeyHour);
      });

  }

  private isFirstJourney(projectTaskHourId: string, startTime: Date) {
    return this.timesheetService.getByDate(startTime).pipe(
      map(hoursToday => {
        return this.isFirstJourneyInHours(projectTaskHourId, startTime, hoursToday);
      }),
    );
  }

  // Prüft Projekt-Fahrzeit grösser als aktuelle Fahrzeit ist und Mitarbeiter-Fahrzeit vorhanden ist
  private canJourneyDeduction(employeeJourneyTime: number, projectJourneyTime: number, duration: number): boolean {
    const durationInMinutes = duration * 60;
    const canDeduct = durationInMinutes > projectJourneyTime && employeeJourneyTime > 0;
    this.log.info(`CanDeduct ${canDeduct}, projectJourney: ${projectJourneyTime}, employeeJourney: ${employeeJourneyTime}, duration: ${durationInMinutes}`);

    return canDeduct;
  }

  // Als Minuseintrag
  private createJourneyDeduction(projectTaskId, projectNumber, startTime, gpsEnd, deduction): Hour {
    const jouneyStartTime = startTime;
    const journeyEndTime = TimeUtil.subMinutes(startTime, deduction);
    const gpsData = gpsEnd
      ? '/' + gpsEnd
      : null;
    return {
      projectTaskHourId: Guid.uuidv4(),
      projectTaskId,
      projectNumber,
      taskName: '',
      startTime: jouneyStartTime,
      endTime: journeyEndTime,
      duration: deduction / 60,
      description: `🚗 -${deduction}min`,
      gpsData,
      taskTypeCode: TaskType.Journey,
      synced: false,
    };
  }

  private isFirstJourneyInHours(projectTaskHourId: string, startTime: Date, hoursToday: Timesheet[]): boolean {
    const workHours = hoursToday
      .filter(h => h.timeSheetId !== projectTaskHourId) // gestartete Stunde rausfiltern
      .filter(h => h.timeSheetTypeCode === 4) // nur Arbeitsstunden
      .filter(h => h.taskTypeCode === TaskType.Journey); // nur Fahrten
    const noWorkHours = workHours.length === 0;
    if (noWorkHours) {
      return true;
    }
    const startedTime = startTime.getTime();
    const firstWorkTime = workHours[0].startTime.getTime();
    return startedTime < firstWorkTime;
  }

  private saveHour(hour: Hour) {
    this.hourService.save([hour])
      .pipe(
        switchMap(updatedHours => this.timesheetService.add(updatedHours)),
      )
      .subscribe();
  }

}